import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

const Typing = ({
    activeList   = 1,
    items        = [],
    indexList    = [],
    processed    = 1,
    setProcessed = () => {}
}) => {
    // constants
    const ERASE_DELAY = 4000;
    const ERASE_SPEED = 10;
    const SPEED = 60;
    const TYPING_DELAY = 100;

    // references
    const containerRef = useRef();

    // state
    const [__timeout, __setTimeout] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentText, setCurrentText] = useState('');
    const [isTyping, setIsTyping] = useState(true);

    // methods
    const erase = () => {
        // run erase on active word to be processed
        if(activeList === processed){
            const displayText = currentText.substring(-currentText.length, currentText.length - 1);
            setCurrentText(displayText);
        }
    };

    const startTyping = () => {
        __setTimeout(
            setTimeout(() => {
                type();
            }, SPEED)
        );
    };

    const type = () => {
        const rawText = items[currentIndex];

        if (currentText.length < rawText.length) {
            const displayText = rawText.substring(0, currentText.length + 1);
            setCurrentText(displayText);
        }
    };

    // hooks
    // on load
    useEffect(() => {
        startTyping();

        return () => {
            __timeout && clearTimeout(__timeout);
        };
    }, []);

    useEffect(() => {
        const rawText = items[currentIndex];

        if (isTyping) {
            if (currentText.length < rawText.length) {
                __setTimeout(setTimeout(type, SPEED));
            } else {
                setIsTyping(false);
                // begin erase
                __setTimeout(setTimeout(erase, ERASE_DELAY * activeList));
            }
        } else {
            if (currentText.length === 0) {
                setTimeout(() => {
                    setProcessed(prev => {
                        const newProcessed = prev === indexList.length ? 1 : prev + 1;
                        return newProcessed;
                    });
                }, 1500);

                const textArray = items;
                const index = currentIndex + 1 === textArray.length ? 0 : currentIndex + 1;
                if (index === currentIndex && activeList === processed) {
                    setIsTyping(true);
                    setTimeout(startTyping, TYPING_DELAY);
                } else {
                    // set new word index
                    setTimeout(() => setCurrentIndex(index), TYPING_DELAY);
                }
            } else {
                // erase individual alphabets
                __setTimeout(setTimeout(erase, ERASE_SPEED));
            }
        }

        return () => {
            __timeout && clearTimeout(__timeout);
        };
    }, [currentText, processed]);

    // start typing new word after erase
    useEffect(() => {
        if (!isTyping) {
            // begin typing active word to be processed
            if(activeList === processed){
                setIsTyping(true);
                startTyping();
            }
        }
        return () => {
            __timeout && clearTimeout(__timeout);
        };
    }, [currentIndex]);

    // render
    return (
        <span
            aria-label={items[currentIndex]}
            className='break-word type-style-2'
            ref={containerRef}>
            {currentText}
        </span>
    );
};

Typing.propTypes = {
    activeList  : PropTypes.number,
    indexList   : PropTypes.array,
    items       : PropTypes.array,
    processed   : PropTypes.number,
    setProcessed: PropTypes.func,
};


/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const TextInteractive = ({
    text       = null,
    staticText = null,
    textList1  = null,
    textList2  = null,
    textList3  = null,
    textList4  = null
}) => {
    // state
    const [processed, setProcessed] = useState(1);

    // misc
    const indexList = [];

    // methods
    const getTextList = (text, idx) => {
        const activeList = indexList.findIndex(index => index === idx) + 1;
        const firstText = text.replace(/\[(.*?)\]/g, (match, val) => val);

        let arrayOfTexts;

        switch (activeList) {
        case 1:
            arrayOfTexts = textList1.split(/,|，/);
            break;
        case 2:
            arrayOfTexts = textList2.split(/,|，/);
            break;
        case 3:
            arrayOfTexts = textList3.split(/,|，/);
            break;
        case 4:
            arrayOfTexts = textList4.split(/,|，/);
            break;
        }

        arrayOfTexts?.unshift(firstText);

        return arrayOfTexts;
    };

    const preparedText = text ? text.slice().replace(/(\S)(\[)/g, '$1 [').replace(/(\])(\S)/g, '] $2') : null;

    // render
    return (
        <section className='module-text-interactive'>
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute top-0 md-relative md-col-2' />

                    {/* content */}
                    <div className='col-11 sm-col-8 md-col-7'>
                        <div className='ml9 sm-ml9 md-ml0'>
                            {staticText && (
                                <h1 className='accessible-hide'>
                                    {staticText}
                                </h1>
                            )}
                            {preparedText && (
                                <div className='px-gutter'>
                                    <div className='rich-text type-style-1 module-text-interactive__text'>
                                        {preparedText.split(' ').map((t, idx) => {
                                            if (t.match(/\[(.*?)\]/)) {
                                                indexList.push(idx);

                                                return <span key={t}>
                                                    <Typing
                                                        activeList={indexList.findIndex(index => index === idx) + 1}
                                                        indexList={indexList}
                                                        items={getTextList(t, idx)}
                                                        processed={processed}
                                                        setProcessed={setProcessed} />
                                                    {' '}
                                                </span>;
                                            } else {
                                                return t + ' ';
                                            }
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    text: PropTypes.string.isRequired,
    staticText: PropTypes.string,
    textList1: PropTypes.string,
    textList2: PropTypes.string,
    textList3: PropTypes.string,
    textList4: PropTypes.string,
};

TextInteractive.propTypes = propTypes;

export { TextInteractive };

export default TextInteractive;
